import React from 'react';
import './TermsOfService.css';

const EFFECTIVE_DATE = 'May 6, 2025';

function PrivacyPolicy({ onBack }) {
  return (
    <div className="Legal-page">
      <div className="Legal-nav">
        <button className="Legal-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          Back
        </button>
        <span className="Legal-nav-brand">PlutoBible</span>
      </div>

      <div className="Legal-content">
        <h1>Privacy Policy</h1>
        <p className="Legal-meta">Effective date: {EFFECTIVE_DATE}</p>

        <p>
          This Privacy Policy describes how <strong>BytePluto Inc.</strong> ("BytePluto", "we", "us",
          or "our"), a corporation incorporated under the laws of the Province of Ontario, Canada,
          collects, uses, discloses, and protects personal information when you use PlutoBible (the
          "Service").
        </p>
        <p>
          We are committed to protecting your privacy in accordance with the <em>Personal Information
          Protection and Electronic Documents Act</em> (PIPEDA) and applicable provincial privacy laws.
          By using the Service, you consent to the practices described in this Privacy Policy.
        </p>

        <h2>1. Information We Collect</h2>
        <p>We collect information in the following ways:</p>

        <h3>1.1 Information You Provide Directly</h3>
        <ul>
          <li>
            <strong>Account information:</strong> When you register, we collect your email address
            and, if you sign in with Google, your name and Google profile information.
          </li>
          <li>
            <strong>User content:</strong> Study notes, bookmarks, and highlights you create within
            the Service are stored on our servers so they are available across your devices.
          </li>
          <li>
            <strong>Support payments:</strong> If you make a voluntary contribution, payment is
            processed by Stripe. We do not receive or store your full credit card number. We may
            receive a transaction confirmation including the amount and a masked payment identifier
            from Stripe.
          </li>
          <li>
            <strong>Communications:</strong> If you contact us by email, we retain your email address
            and the content of your message to respond to your inquiry.
          </li>
        </ul>

        <h3>1.2 Information Collected Automatically</h3>
        <ul>
          <li>
            <strong>Authentication tokens:</strong> Firebase generates and manages secure authentication
            tokens tied to your account. These are stored locally on your device and sent to our
            servers only to verify your identity on authenticated requests.
          </li>
          <li>
            <strong>Browser storage:</strong> The Service uses your browser's local storage to save
            preferences such as font size, font family, selected Bible version, and AI chat history.
            This data never leaves your device unless it corresponds to a feature that explicitly
            syncs with our servers (such as notes and bookmarks).
          </li>
          <li>
            <strong>Cached content:</strong> Downloaded Bible versions and AI model weights (for
            in-browser AI) are cached in your browser's Cache Storage or IndexedDB to enable offline
            use and faster loading. This data is stored entirely on your device.
          </li>
        </ul>

        <h3>1.3 Information We Do Not Collect</h3>
        <ul>
          <li>We do not collect precise geolocation data.</li>
          <li>
            We do not collect or store the content of your AI chat conversations on our servers.
            AI chat history is stored only in your browser's local storage on your own device.
          </li>
          <li>
            When you use on-device AI features (Ollama or in-browser WebLLM), your queries are
            processed locally and are not sent to BytePluto or any external AI provider.
          </li>
          <li>We do not sell your personal information to any third party.</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Create and manage your account and authenticate your identity;</li>
          <li>Sync and retrieve your notes, bookmarks, and highlights across devices;</li>
          <li>Process voluntary support contributions through Stripe;</li>
          <li>Respond to your support requests and communications;</li>
          <li>Maintain the security and integrity of the Service;</li>
          <li>Improve and develop features of the Service;</li>
          <li>
            Comply with legal obligations and enforce our Terms of Service.
          </li>
        </ul>
        <p>
          We do not use your personal information for advertising profiling, automated
          decision-making that produces legal effects, or the training of AI or machine learning models.
        </p>

        <h2>3. How We Share Your Information</h2>
        <p>
          We do not sell, rent, or trade your personal information. We may share information only in
          the following limited circumstances:
        </p>
        <ul>
          <li>
            <strong>Service providers:</strong> We share information with trusted third-party service
            providers who assist in operating the Service (see Section 5), strictly for the purpose
            of providing those services to you.
          </li>
          <li>
            <strong>Legal requirements:</strong> We may disclose your information if required by law,
            court order, or governmental authority, or if we believe in good faith that such disclosure
            is necessary to protect our rights, your safety, or the safety of others.
          </li>
          <li>
            <strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of
            all or substantially all of our assets, your information may be transferred as part of
            that transaction. We will notify you by email or prominent notice within the Service
            before your information becomes subject to a different privacy policy.
          </li>
        </ul>

        <h2>4. Data Storage and Security</h2>
        <p>
          Your account information and user content (notes, bookmarks, highlights) are stored on
          servers hosted by <strong>DigitalOcean</strong> (PostgreSQL database) and
          <strong>Google Firebase</strong> (authentication and file storage), with infrastructure
          located in their respective data centres. DigitalOcean and Google maintain industry-standard
          physical and technical security measures.
        </p>
        <p>
          We implement reasonable administrative and technical safeguards to protect your personal
          information against unauthorized access, disclosure, alteration, or destruction. However,
          no method of transmission over the internet or electronic storage is 100% secure. We
          encourage you to use a strong, unique password for your account.
        </p>
        <p>
          All data transmitted between your browser and our servers is encrypted using
          HTTPS/TLS.
        </p>

        <h2>5. Third-Party Services</h2>
        <p>
          The Service integrates with the following third-party services, each governed by their
          own privacy policies:
        </p>
        <ul>
          <li>
            <strong>Google Firebase</strong> (Google LLC) — authentication and cloud storage.
            Google's Privacy Policy applies to data processed through Firebase.
          </li>
          <li>
            <strong>Stripe</strong> — payment processing for voluntary contributions. Stripe's
            Privacy Policy governs payment data. We never store full payment card details.
          </li>
          <li>
            <strong>Google AdSense</strong> — advertising displayed on the Service. Google may use
            cookies to serve ads based on your prior visits to websites. You can opt out of
            personalized advertising via Google's Ad Settings.
          </li>
          <li>
            <strong>Google Fonts</strong> — fonts loaded from Google's CDN. Google may log your
            IP address when fonts are requested.
          </li>
          <li>
            <strong>Ollama / WebLLM (on-device AI)</strong> — when you use AI chat features
            with on-device models, all processing happens locally on your device. No query
            content is sent to BytePluto or any external server.
          </li>
        </ul>

        <h2>6. Cookies and Local Storage</h2>
        <p>
          The Service uses browser local storage (not traditional cookies) to save your preferences
          (font, version, theme) and AI chat history on your device. Firebase authentication uses
          local storage to maintain your login session.
        </p>
        <p>
          Google AdSense and Google Fonts may set cookies on your device for their own purposes.
          You can manage or disable cookies through your browser settings; however, disabling
          cookies may affect the functionality of third-party integrations.
        </p>

        <h2>7. Data Retention</h2>
        <p>
          We retain your account information and user content for as long as your account is active
          or as needed to provide the Service. If you delete your account, we will delete or
          anonymize your personal information within 30 days, except where retention is required by
          law or for legitimate business purposes such as fraud prevention.
        </p>
        <p>
          Data stored in your browser (local storage, cache) is controlled by you and can be cleared
          at any time through your browser settings.
        </p>

        <h2>8. Your Privacy Rights</h2>
        <p>
          Under PIPEDA and applicable provincial law, you have the right to:
        </p>
        <ul>
          <li>
            <strong>Access</strong> — request a copy of the personal information we hold about you;
          </li>
          <li>
            <strong>Correction</strong> — request that we correct inaccurate or incomplete
            information;
          </li>
          <li>
            <strong>Deletion</strong> — request deletion of your personal information, subject to
            legal retention obligations;
          </li>
          <li>
            <strong>Withdraw consent</strong> — withdraw your consent to our collection or use of
            your personal information at any time (note this may limit your ability to use the
            Service);
          </li>
          <li>
            <strong>Complaint</strong> — lodge a complaint with the Office of the Privacy
            Commissioner of Canada (OPC) if you believe your privacy rights have been violated.
          </li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at{' '}
          <a href="mailto:apps-support@bytepluto.org">apps-support@bytepluto.org</a>. We will
          respond within 30 days.
        </p>

        <h2>9. Children's Privacy</h2>
        <p>
          The Service is not directed to children under the age of 13. We do not knowingly collect
          personal information from children under 13. If you are a parent or guardian and believe
          your child has provided us with personal information without your consent, please contact
          us at <a href="mailto:apps-support@bytepluto.org">apps-support@bytepluto.org</a> and we
          will promptly delete such information.
        </p>

        <h2>10. International Users</h2>
        <p>
          PlutoBible is operated from Canada. If you access the Service from outside Canada, your
          information may be transferred to and processed in Canada and in countries where our
          service providers operate (including the United States). By using the Service, you consent
          to this transfer. We take steps to ensure that personal information receives a comparable
          level of protection wherever it is processed.
        </p>

        <h2>11. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will update the
          effective date at the top of this page. For material changes, we will make reasonable
          efforts to notify you — for example, by displaying a notice in the app or sending an
          email to your registered address. Your continued use of the Service after any update
          constitutes acceptance of the revised Privacy Policy.
        </p>

        <h2>12. Contact Us</h2>
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our
          privacy practices, please contact our Privacy Officer at:
        </p>
        <address>
          <strong>BytePluto Inc. — Privacy Officer</strong><br />
          Toronto, Ontario, Canada<br />
          <a href="mailto:apps-support@bytepluto.org">apps-support@bytepluto.org</a>
        </address>
        <p style={{ marginTop: '1rem' }}>
          You may also contact the <strong>Office of the Privacy Commissioner of Canada</strong> at{' '}
          <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">www.priv.gc.ca</a>{' '}
          if you have unresolved concerns.
        </p>
      </div>

      <footer className="Legal-footer">
        <p>&copy; {new Date().getFullYear()} BytePluto Inc. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
